import dayjs from "dayjs";

/**
 * A clock time to compare against the wall clock — with the date attached once
 * it is not today, because a bare "10:03" reads as this morning and yesterday's
 * build is exactly the case where that misleads.
 */
export const formatMoment = (value, now) => {
  if (!value) return "";
  const at = dayjs(value);
  if (!at.isValid()) return "";
  return at.isSame(dayjs(now), "day") ? at.format("HH:mm") : at.format("D MMM, HH:mm");
};

/** Whole minutes left until `value`, never negative — an overdue build is "now". */
export const minutesUntil = (value, now) => {
  if (!value) return null;
  const due = dayjs(value);
  if (!due.isValid()) return null;
  return Math.max(0, Math.ceil((due.valueOf() - dayjs(now).valueOf()) / 60000));
};

/**
 * The one sentence the panel puts in front of the editor, as an i18n key plus
 * its values — never as finished text, so the decision stays testable without
 * a translator loaded.
 *
 * The order the states are decided in is the whole point of the screen. A
 * publish here is silent for minutes, and "the countdown is running" has to be
 * distinguishable at a glance from "the build token expired weeks ago".
 */
export const describeRebuild = (status, now = Date.now()) => {
  const lastResult = status?.lastResult ?? null;
  // Only a settled attempt can have failed; a queued one has not reported yet.
  const failure = lastResult && lastResult.ok === false ? lastResult.message || "" : null;
  const base = { reasons: [], failure: null };

  // Off first, even with markers already waiting: where nothing builds, a
  // promise that the changes appear at 10:03 is one nothing keeps. A stale
  // failure is dropped with it — it says nothing about an environment that
  // was never going to try.
  if (status && (status.enabled === false || status.configured === false)) {
    return { ...base, state: "off", tone: "info", key: "rebuild_state_off", values: {} };
  }

  if (status?.pending) {
    const time = formatMoment(status.dueAt, now);
    const minutes = minutesUntil(status.dueAt, now);
    return {
      ...base,
      state: "pending",
      tone: "info",
      // "Soon" covers both ends of the same problem: a queue the cron has not
      // stamped a due time on yet, and one already due, where naming a clock
      // time that has passed or a wait of zero minutes reads as a fault.
      key: time && minutes > 0 ? "rebuild_state_pending" : "rebuild_state_pending_soon",
      values: { time, minutes },
      reasons: status.pendingReasons ?? [],
      // Pending takes the headline because a retry is already scheduled, but a
      // previous failure still shows underneath it: a countdown that will run
      // into the same dead token should not look like a healthy one.
      failure: failure || null,
    };
  }

  if (failure !== null) {
    return {
      ...base,
      state: "failed",
      tone: "error",
      key: "rebuild_state_failed",
      values: {},
      failure,
    };
  }

  if (lastResult?.ok) {
    return {
      ...base,
      state: "ok",
      tone: "success",
      key: "rebuild_state_ok",
      values: { time: formatMoment(status.lastTriggeredAt || lastResult.at, now) },
    };
  }

  return { ...base, state: "idle", tone: "info", key: "rebuild_state_idle", values: {} };
};
